/**
 * Recruitment / ATS: job postings, candidates, and the application pipeline between them.
 *
 * Every route here is gated on the `performance_reviews` matrix cell but narrowed
 * underneath it, on every read as well as every write, to HR Manager and Super Admin --
 * see `recruitment_service._require_recruitment_role`. There is no finer matrix cell to
 * hide a button behind, so a screen that reaches these endpoints without that role gets
 * the same 403 every write dialog in this app already knows how to surface.
 *
 * `stage` moves only through `advanceStage` (`POST /applications/{id}/stage`) -- the
 * plain `updateApplication` patch has no `stage` field at all, so the pipeline's
 * forward-only graph can never be bypassed by a generic edit.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'

export const JOB_POSTING_STATUSES = ['open', 'closed', 'on_hold'] as const
export type JobPostingStatus = (typeof JOB_POSTING_STATUSES)[number]

export const JOB_POSTING_STATUS_LABELS: Record<JobPostingStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  on_hold: 'On hold',
}

export const CANDIDATE_SOURCES = [
  'referral',
  'job_board',
  'company_website',
  'agency',
  'other',
] as const
export type CandidateSource = (typeof CANDIDATE_SOURCES)[number]

export const CANDIDATE_SOURCE_LABELS: Record<CandidateSource, string> = {
  referral: 'Referral',
  job_board: 'Job board',
  company_website: 'Company website',
  agency: 'Agency',
  other: 'Other',
}

/** Forward-only, mirrors `recruitment_service.ALLOWED_STAGE_TRANSITIONS`. A stage absent
 *  as a key here (hired, rejected) has no further move. */
export const APPLICATION_STAGES = [
  'applied',
  'screening',
  'interview',
  'offer',
  'hired',
  'rejected',
] as const
export type ApplicationStage = (typeof APPLICATION_STAGES)[number]

export const APPLICATION_STAGE_LABELS: Record<ApplicationStage, string> = {
  applied: 'Applied',
  screening: 'Screening',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
}

export const ALLOWED_STAGE_TRANSITIONS: Record<ApplicationStage, ApplicationStage[]> = {
  applied: ['screening', 'rejected'],
  screening: ['interview', 'rejected'],
  interview: ['offer', 'rejected'],
  offer: ['hired', 'rejected'],
  hired: [],
  rejected: [],
}

// --------------------------------------------------------------------------- job postings

export interface JobPostingCreate {
  title: string
  department_id: string
  location_id?: string | null
  status?: JobPostingStatus
  description?: string | null
}

export type JobPostingUpdate = Partial<JobPostingCreate>

export interface JobPostingRead {
  id: string
  title: string
  department_id: string
  department_name: string | null
  location_id: string | null
  status: JobPostingStatus
  description: string | null
  created_by: string | null
  closed_at: string | null
  created_at: string
  updated_at: string
}

export interface JobPostingFilters {
  page?: number
  department_id?: string
  status?: JobPostingStatus | ''
  q?: string
}

const POSTINGS_BASE = `${API_PREFIX}/job-postings`

function postingQuery(filters: JobPostingFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.department_id) params.set('department_id', filters.department_id)
  if (filters.status) params.set('status', filters.status)
  if (filters.q) params.set('q', filters.q)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listJobPostings = (filters: JobPostingFilters = {}) =>
  apiFetch<Page<JobPostingRead>>(`${POSTINGS_BASE}${postingQuery(filters)}`)

export const getJobPosting = (id: string) => apiGet<JobPostingRead>(`${POSTINGS_BASE}/${id}`)

export const createJobPosting = (data: JobPostingCreate) =>
  apiGet<JobPostingRead>(POSTINGS_BASE, { method: 'POST', body: JSON.stringify(data) })

export const updateJobPosting = (id: string, data: JobPostingUpdate) =>
  apiGet<JobPostingRead>(`${POSTINGS_BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(data) })

export const deleteJobPosting = (id: string) =>
  apiFetch<void>(`${POSTINGS_BASE}/${id}`, { method: 'DELETE' })

export const exportJobPostings = (filters: JobPostingFilters, format: ExportFormat) => {
  const params = new URLSearchParams({ format })
  if (filters.department_id) params.set('department_id', filters.department_id)
  if (filters.status) params.set('status', filters.status)
  return apiBlob(`${POSTINGS_BASE}/export?${params}`)
}

/** Just enough of `inventory.Location` for the job posting form's picker. Fetched from a
 *  recruitment-owned endpoint, not `@/features/orders/api`'s `listLocations` -- `hr_manager`
 *  has no rights on the `inventory` matrix cell that one is gated on. */
export interface LocationOption {
  id: string
  code: string
  name: string
}

export const listJobPostingLocations = () =>
  apiGet<LocationOption[]>(`${POSTINGS_BASE}/locations`)

// ----------------------------------------------------------------------------- candidates

export interface CandidateCreate {
  full_name: string
  email: string
  phone?: string | null
  resume_key?: string | null
  source?: CandidateSource | null
  notes?: string | null
}

/** `email` is absent: it is the partial-unique key a re-application is matched on, so
 *  changing it is a new candidate, not an edit to this one -- see the API schema note. */
export type CandidateUpdate = Partial<Omit<CandidateCreate, 'email'>>

export interface CandidateRead {
  id: string
  full_name: string
  email: string
  phone: string | null
  resume_key: string | null
  resume_url: string | null
  source: CandidateSource | null
  notes: string | null
  created_at: string
  updated_at: string
}

export interface CandidateFilters {
  page?: number
  source?: CandidateSource | ''
  q?: string
}

const CANDIDATES_BASE = `${API_PREFIX}/candidates`

function candidateQuery(filters: CandidateFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.source) params.set('source', filters.source)
  if (filters.q) params.set('q', filters.q)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listCandidates = (filters: CandidateFilters = {}) =>
  apiFetch<Page<CandidateRead>>(`${CANDIDATES_BASE}${candidateQuery(filters)}`)

export const getCandidate = (id: string) => apiGet<CandidateRead>(`${CANDIDATES_BASE}/${id}`)

export const createCandidate = (data: CandidateCreate) =>
  apiGet<CandidateRead>(CANDIDATES_BASE, { method: 'POST', body: JSON.stringify(data) })

export const updateCandidate = (id: string, data: CandidateUpdate) =>
  apiGet<CandidateRead>(`${CANDIDATES_BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(data) })

export const deleteCandidate = (id: string) =>
  apiFetch<void>(`${CANDIDATES_BASE}/${id}`, { method: 'DELETE' })

export const exportCandidates = (filters: CandidateFilters, format: ExportFormat) => {
  const params = new URLSearchParams({ format })
  if (filters.source) params.set('source', filters.source)
  return apiBlob(`${CANDIDATES_BASE}/export?${params}`)
}

// --------------------------------------------------------------------------- applications

export interface ApplicationCreate {
  candidate_id: string
  job_posting_id: string
  notes?: string | null
}

/** `stage` is deliberately absent -- see `advanceStage`. */
export interface ApplicationUpdate {
  notes?: string | null
}

export interface ApplicationRead {
  id: string
  candidate_id: string
  candidate_name: string | null
  job_posting_id: string
  job_posting_title: string | null
  stage: ApplicationStage
  applied_at: string
  current_stage_since: string
  rejection_reason: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export interface ApplicationFilters {
  page?: number
  candidate_id?: string
  job_posting_id?: string
  stage?: ApplicationStage | ''
}

const APPLICATIONS_BASE = `${API_PREFIX}/applications`

function applicationQuery(filters: ApplicationFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.candidate_id) params.set('candidate_id', filters.candidate_id)
  if (filters.job_posting_id) params.set('job_posting_id', filters.job_posting_id)
  if (filters.stage) params.set('stage', filters.stage)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listApplications = (filters: ApplicationFilters = {}) =>
  apiFetch<Page<ApplicationRead>>(`${APPLICATIONS_BASE}${applicationQuery(filters)}`)

export const getApplication = (id: string) => apiGet<ApplicationRead>(`${APPLICATIONS_BASE}/${id}`)

export const createApplication = (data: ApplicationCreate) =>
  apiGet<ApplicationRead>(APPLICATIONS_BASE, { method: 'POST', body: JSON.stringify(data) })

export const updateApplication = (id: string, data: ApplicationUpdate) =>
  apiGet<ApplicationRead>(`${APPLICATIONS_BASE}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })

/** The only way `stage` moves -- see `recruitment_service.transition_stage`. */
export const advanceStage = (id: string, stage: ApplicationStage, rejection_reason?: string) =>
  apiGet<ApplicationRead>(`${APPLICATIONS_BASE}/${id}/stage`, {
    method: 'POST',
    body: JSON.stringify({ stage, rejection_reason: rejection_reason ?? null }),
  })

export const deleteApplication = (id: string) =>
  apiFetch<void>(`${APPLICATIONS_BASE}/${id}`, { method: 'DELETE' })

export const exportApplications = (filters: ApplicationFilters, format: ExportFormat) => {
  const params = new URLSearchParams({ format })
  if (filters.candidate_id) params.set('candidate_id', filters.candidate_id)
  if (filters.job_posting_id) params.set('job_posting_id', filters.job_posting_id)
  if (filters.stage) params.set('stage', filters.stage)
  return apiBlob(`${APPLICATIONS_BASE}/export?${params}`)
}
